import { useState, useEffect, useCallback } from 'react';
import { supabase, isSupabaseConfigured, transformTaskToCamelCase, transformTaskToSnakeCase } from '../utils/supabase';
import { updateDealNextActionDue } from './useTasks';

/**
 * Hook for fetching tasks related to a specific deal.
 * 
 * Unlike useTasks which filters by assignedTo, this hook queries directly
 * by deal_id to ensure ALL tasks for a deal are shown regardless
 * of who they are assigned to.
 * 
 * @param {string} dealId - The Supabase UUID of the deal to fetch tasks for
 * @returns {object} - { tasks, loading, createTask, updateTask, completeTask, deleteTask, reopenTask }
 */
export function useDealTasks(dealId) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTasks = useCallback(async () => {
    if (!dealId || !isSupabaseConfigured()) {
      setTasks([]);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('crm_tasks')
        .select(`
          *,
          crm_contacts!contact_id(id, first_name, last_name, email)
        `)
        .eq('deal_id', dealId)
        .order('due_date', { ascending: true });

      if (error) {
        console.error('[useDealTasks] Error fetching tasks:', error);
        setTasks([]);
      } else {
        const taskList = (data || []).map(transformTaskToCamelCase);
        setTasks(taskList);
      }
    } catch (error) {
      console.error('[useDealTasks] Unexpected error:', error);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    fetchTasks();

    // Set up real-time subscription
    if (!dealId || !isSupabaseConfigured()) return;

    const channel = supabase
      .channel(`deal-tasks-${dealId}-${Math.random().toString(36).slice(2, 8)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_tasks',
          filter: `deal_id=eq.${dealId}`,
        },
        () => {
          // Refetch to get fresh data with joins
          fetchTasks();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [dealId, fetchTasks]);

  const createTask = async (taskData) => {
    if (!dealId) {
      return { success: false, error: 'No deal ID provided' };
    }

    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase not configured' };
    }

    try {
      // Ensure createdBy and assignedTo are set (handle assignedBy -> createdBy fallback)
      const createdBy = taskData.createdBy || taskData.assignedBy || taskData.assignedTo;
      const assignedTo = taskData.assignedTo || taskData.createdBy || taskData.assignedBy;

      // Validate that we have a user ID - created_by is NOT NULL in the database
      if (!createdBy) {
        console.error('[useDealTasks] Missing user ID for task creation:', { 
          createdBy: taskData.createdBy, 
          assignedBy: taskData.assignedBy, 
          assignedTo: taskData.assignedTo 
        });
        return { success: false, error: 'User ID is required to create a task. Please ensure you are logged in.' };
      }

      const newTask = transformTaskToSnakeCase({
        ...taskData,
        createdBy,
        assignedTo: assignedTo || createdBy, // Fallback assignedTo to createdBy if still missing
        status: taskData.status || 'pending',
        priority: taskData.priority || 'medium',
        dealId: dealId,
        // Every task created through this hook belongs to the deal, so make
        // sure the related-entity link is set even when the caller omits it.
        relatedTo: taskData.relatedTo || {
          type: 'deal',
          id: dealId,
          name: taskData.relatedEntityName || 'Deal',
        },
      });

      console.log('[useDealTasks] Creating task with data:', { 
        createdBy, 
        assignedTo: assignedTo || createdBy, 
        dealId,
        newTask // Log the actual transformed object being sent to Supabase
      });

      const { data, error } = await supabase
        .from('crm_tasks')
        .insert(newTask)
        .select('*')
        .single();

      if (error) {
        console.error('[useDealTasks] Error creating task:', error);
        return { success: false, error: error.message };
      }

      // Immediately update local state with the new task
      const newTaskCamelCase = transformTaskToCamelCase(data);
      setTasks(prev => [...prev, newTaskCamelCase].sort((a, b) => {
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return new Date(a.dueDate) - new Date(b.dueDate);
      }));

      // Update the deal's next_action_due
      await updateDealNextActionDue(dealId);

      return { success: true, id: data.id };
    } catch (error) {
      console.error('[useDealTasks] Error creating task:', error);
      return { success: false, error: error.message };
    }
  };

  const updateTask = async (taskId, updates) => {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase not configured' };
    }

    try {
      const updateData = transformTaskToSnakeCase(updates);
      // Remove id from updates if present
      delete updateData.id;

      const { error } = await supabase
        .from('crm_tasks')
        .update(updateData)
        .eq('id', taskId);

      if (error) {
        console.error('[useDealTasks] Error updating task:', error);
        return { success: false, error: error.message };
      }

      // Immediately update local state
      setTasks(prev => prev.map(t => 
        t.id === taskId 
          ? { ...t, ...updates }
          : t
      ));

      // Update the deal's next_action_due if due date or status changed
      if (updates.dueDate !== undefined || updates.status !== undefined) {
        await updateDealNextActionDue(dealId);
      }

      return { success: true };
    } catch (error) {
      console.error('[useDealTasks] Error updating task:', error);
      return { success: false, error: error.message };
    }
  };

  const completeTask = async (taskId) => {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase not configured' };
    }

    try {
      // Complete the task. If it was synced to Google Calendar, the DB trigger
      // enqueues the removal and the server takes it off the calendar.
      const completedAt = new Date().toISOString();
      const { error } = await supabase
        .from('crm_tasks')
        .update({
          status: 'completed',
          completed_at: completedAt,
        })
        .eq('id', taskId);

      if (error) {
        console.error('[useDealTasks] Error completing task:', error);
        return { success: false, error: error.message };
      }

      // Immediately update local state
      setTasks(prev => prev.map(t => 
        t.id === taskId 
          ? { ...t, status: 'completed', completedAt: new Date(completedAt) }
          : t
      ));

      // Update the deal's next_action_due
      await updateDealNextActionDue(dealId);

      return { success: true };
    } catch (error) {
      console.error('[useDealTasks] Error completing task:', error);
      return { success: false, error: error.message };
    }
  };

  const deleteTask = async (taskId) => {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase not configured' };
    }

    try {
      const { error } = await supabase
        .from('crm_tasks')
        .delete()
        .eq('id', taskId);

      if (error) {
        console.error('[useDealTasks] Error deleting task:', error);
        return { success: false, error: error.message };
      }

      // Immediately remove from local state
      setTasks(prev => prev.filter(t => t.id !== taskId));

      // Update the deal's next_action_due
      await updateDealNextActionDue(dealId);

      return { success: true };
    } catch (error) {
      console.error('[useDealTasks] Error deleting task:', error);
      return { success: false, error: error.message };
    }
  };

  const reopenTask = async (taskId) => {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase not configured' };
    }

    try {
      const { error } = await supabase
        .from('crm_tasks')
        .update({
          status: 'pending',
          completed_at: null,
        })
        .eq('id', taskId);

      if (error) {
        console.error('[useDealTasks] Error reopening task:', error);
        return { success: false, error: error.message };
      }

      // Immediately update local state
      setTasks(prev => prev.map(t => 
        t.id === taskId 
          ? { ...t, status: 'pending', completedAt: null }
          : t
      ));

      // Update the deal's next_action_due
      await updateDealNextActionDue(dealId);

      return { success: true };
    } catch (error) {
      console.error('[useDealTasks] Error reopening task:', error);
      return { success: false, error: error.message };
    }
  };

  return {
    tasks,
    loading,
    createTask,
    updateTask,
    completeTask,
    deleteTask,
    reopenTask,
  };
}
